// The ONE highlighted comment of an article's conversation (2026-10-24): drawn first in the
// panel, quoted by the mobile bar and the rail card. Pure — the widget and the preview both
// import it, so the three surfaces can never pick different comments from the same data.
import type { CommentHubComment } from 'shared-code/commenthub-api'

export type HighlightKind = 'featured' | 'top'

export interface HighlightComment {
  comment: CommentHubComment
  kind: HighlightKind
}

/**
 * How many threads of `sort=top` page 1 are candidates for "Top comment" — the panel's page size,
 * and the preview's extra request. commentHub's `top` is the ranking (upvotes − ½ downvotes +
 * 2 × replies, ties newest first — comment-hub `threadScore`); the highlight takes its order as
 * is and never re-ranks, so "Top comment" and the Top sort can never disagree.
 */
export const HIGHLIGHT_CANDIDATES = 25

/** The panel's label, before the time row. */
export const HIGHLIGHT_LABELS: Record<HighlightKind, string> = {
  // An editor's featured pick reads "Pinned" since 2026-10-29: one word for one idea.
  featured: 'Pinned',
  top: 'Top comment',
}

/**
 * A comment every reader sees: not deleted (listed with a scrubbed body) and not held for review
 * (listed to its author only). The ONE "live" rule of the conversation — the highlight, the
 * preview (newest, the last hour, the end block's faces) and the panel's Newest merge use it.
 */
export function isLiveComment(comment: CommentHubComment): boolean {
  return !comment.deleted && !comment.pendingReview
}

/** Live replies of a thread across every nesting level (commentHub's `threadReplyCount`). */
export function liveReplyCount(comment: CommentHubComment): number {
  return comment.replies.reduce(
    (n, reply) => n + (isLiveComment(reply) ? 1 : 0) + liveReplyCount(reply),
    0
  )
}

/**
 * The editor's pick if there is one (`featured` is newest pick first — the first live one wins,
 * whatever its engagement); otherwise the first live candidate in commentHub's `top` order that
 * has at least one upvote or one reply. Null = no highlight. `candidates` must be `sort=top`.
 */
/**
 * The moderators' picks of a list, in the order the panel draws them: the editor's featured
 * picks (every page), then the pinned block (page 1). One concept since 2026-10-29 — a pinned
 * comment is the highlight exactly as a featured one is, and reads "Pinned".
 */
export function highlightPicks(list: {
  featured?: readonly CommentHubComment[] | null
  pinned?: readonly CommentHubComment[] | null
}): CommentHubComment[] {
  return [...(list.featured ?? []), ...(list.pinned ?? [])]
}

export function pickHighlightComment(
  featured: readonly CommentHubComment[] | null | undefined,
  candidates: readonly CommentHubComment[]
): HighlightComment | null {
  const pick = featured?.find(isLiveComment)
  if (pick) {
    return { comment: pick, kind: 'featured' }
  }
  const best = candidates.find(
    comment => isLiveComment(comment) && (comment.upvotes > 0 || liveReplyCount(comment) > 0)
  )
  return best ? { comment: best, kind: 'top' } : null
}

/**
 * The list without the highlighted comment, so it is drawn once: a top-level highlight leaves
 * the list; a featured REPLY (commentHub keeps it inside its thread too) leaves its parent's
 * replies, its own replies travelling with it to the top. Drawing only — the paged state and
 * its counts are untouched.
 */
export function withoutComment(
  list: readonly CommentHubComment[],
  id: number | null | undefined
): CommentHubComment[] {
  if (id === null || id === undefined) {
    return [...list]
  }
  const contains = (c: CommentHubComment): boolean => c.id === id || c.replies.some(contains)
  return list
    .filter(c => c.id !== id)
    .map(c => (c.replies.some(contains) ? { ...c, replies: withoutComment(c.replies, id) } : c))
}
